import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { 
  DndContext, 
  DragEndEvent, 
  DragOverlay, 
  DragStartEvent,
  PointerSensor,
  useSensor,
  useSensors,
  closestCenter
} from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { 
  Plus, 
  Trash2, 
  GripVertical, 
  Edit, 
  Copy, 
  Send,
  Eye,
  Save,
  X,
  AlertCircle
} from 'lucide-react';
import { InteractiveMenu, InteractiveMenuSection, InteractiveMenuRow, CreateInteractiveMenuRequest, UpdateInteractiveMenuRequest } from '@/types/bi';
import { useToast } from '@/hooks/use-toast';
import { AtomicLoadingScreen } from '@/components/ui/atomic-loader';
import biApi from '@/services/biApi';
import { apiService } from '@/services/api';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

interface InteractiveMenuBuilderProps {
  clientId?: number;
}

// Componente Sortable para Seção
interface SortableSectionProps {
  section: InteractiveMenuSection;
  index: number;
  totalRows: number;
  onUpdate: (section: InteractiveMenuSection) => void;
  onDelete: () => void;
  onAddRow: () => void;
  onUpdateRow: (rowIndex: number, row: InteractiveMenuRow) => void;
  onDeleteRow: (rowIndex: number) => void;
}

const SortableSection: React.FC<SortableSectionProps> = ({
  section,
  index,
  totalRows,
  onUpdate,
  onDelete,
  onAddRow,
  onUpdateRow,
  onDeleteRow
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({ id: section.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1
  };

  const canAddRow = totalRows < 10;
  const canAddRowInSection = section.rows.length < 10;

  return (
    <div ref={setNodeRef} style={style} className="mb-4">
      <Card className="border-l-4 border-l-blue-500">
        <CardHeader className="pb-3">
          <div className="flex items-center gap-2">
            <div {...attributes} {...listeners} className="cursor-grab active:cursor-grabbing">
              <GripVertical className="h-5 w-5 text-gray-400" />
            </div>
            <div className="flex-1">
              <Label className="text-sm font-semibold">Seção {index + 1}</Label>
              <Input
                value={section.title}
                onChange={(e) => {
                  const value = e.target.value.slice(0, 24);
                  onUpdate({ ...section, title: value });
                }}
                placeholder="Título da seção (máx 24 caracteres)"
                className="mt-1"
                maxLength={24}
              />
              <div className="flex items-center justify-between mt-1">
                <span className="text-xs text-gray-500">{section.title.length}/24</span>
                <Badge variant="secondary" className="text-xs">
                  {section.rows.length} row{section.rows.length !== 1 ? 's' : ''}
                </Badge>
              </div>
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={onDelete}
              className="text-destructive hover:text-destructive"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <SortableContext items={section.rows.map((_, idx) => `row-${section.id}-${idx}`)} strategy={verticalListSortingStrategy}>
            <div className="space-y-3">
              {section.rows.map((row, rowIndex) => (
                <SortableRow
                  key={`row_${section.id}_${rowIndex}`}
                  id={`row_${section.id}_${rowIndex}`}
                  sectionId={section.id}
                  row={row}
                  onUpdate={(updatedRow) => onUpdateRow(rowIndex, updatedRow)}
                  onDelete={() => onDeleteRow(rowIndex)}
                />
              ))}
            </div>
          </SortableContext>
          
          <Button
            variant="outline"
            size="sm"
            onClick={onAddRow}
            disabled={!canAddRow || !canAddRowInSection}
            className="w-full mt-3"
          >
            <Plus className="h-4 w-4 mr-2" />
            Adicionar Row {!canAddRow && '(limite de 10 rows atingido)'}
            {canAddRow && !canAddRowInSection && '(limite de 10 rows por seção)'}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};

// Componente Sortable para Row
interface SortableRowProps {
  id: string;
  sectionId: string;
  row: InteractiveMenuRow;
  onUpdate: (row: InteractiveMenuRow) => void;
  onDelete: () => void;
}

const SortableRow: React.FC<SortableRowProps> = ({ id, sectionId, row, onUpdate, onDelete }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1
  };

  return (
    <div ref={setNodeRef} style={style}>
      <Card className="bg-gray-50 border-gray-200">
        <CardContent className="p-4">
          <div className="flex items-start gap-2">
            <div {...attributes} {...listeners} className="cursor-grab active:cursor-grabbing mt-2">
              <GripVertical className="h-4 w-4 text-gray-400" />
            </div>
            <div className="flex-1 space-y-3">
              <div>
                <Label className="text-xs text-gray-600">ID da Row</Label>
                <Input
                  value={row.id}
                  onChange={(e) => {
                    const value = e.target.value.slice(0, 200);
                    onUpdate({ ...row, id: value });
                  }}
                  placeholder="row_1"
                  className="mt-1 text-sm"
                  maxLength={200}
                />
                <span className="text-xs text-gray-500">{row.id.length}/200</span>
              </div>
              
              <div>
                <Label className="text-xs text-gray-600">Título</Label>
                <Input
                  value={row.title}
                  onChange={(e) => {
                    const value = e.target.value.slice(0, 24);
                    onUpdate({ ...row, title: value });
                  }}
                  placeholder="Ex: Segunda via"
                  className="mt-1 text-sm"
                  maxLength={24}
                />
                <span className="text-xs text-gray-500">{row.title.length}/24</span>
              </div>
              
              <div>
                <Label className="text-xs text-gray-600">Descrição (opcional)</Label>
                <Input
                  value={row.description || ''}
                  onChange={(e) => {
                    const value = e.target.value.slice(0, 72);
                    onUpdate({ ...row, description: value || undefined });
                  }}
                  placeholder="Ex: Solicitar segunda via do boleto"
                  className="mt-1 text-sm"
                  maxLength={72}
                />
                <span className="text-xs text-gray-500">{(row.description || '').length}/72</span>
              </div>
            </div>
            
            <Button
              variant="ghost"
              size="icon"
              onClick={onDelete}
              className="text-destructive hover:text-destructive mt-2"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

const InteractiveMenuBuilder: React.FC<InteractiveMenuBuilderProps> = ({ clientId }) => {
  const { toast } = useToast();
  const [menus, setMenus] = useState<InteractiveMenu[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [isSendModalOpen, setIsSendModalOpen] = useState(false);
  const [sendingMenu, setSendingMenu] = useState<InteractiveMenu | null>(null);
  const [sendPhoneNumber, setSendPhoneNumber] = useState('');
  const [sendConnectionId, setSendConnectionId] = useState<number | null>(null);
  const [editingMenu, setEditingMenu] = useState<InteractiveMenu | null>(null);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [whatsappConnections, setWhatsappConnections] = useState<Array<{ id: number; name: string }>>([]);
  
  // Estado do formulário
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    button_text: '',
    header_text: '',
    has_header: false,
    body_text: '',
    footer_text: '',
    has_footer: false,
    sections: [] as InteractiveMenuSection[]
  });

  const sensors = useSensor(PointerSensor, {
    activationConstraint: {
      distance: 8,
    },
  });

  // Carregar menus e conexões
  useEffect(() => {
    if (clientId) {
      loadMenus();
      loadWhatsappConnections();
    }
  }, [clientId]);

  const loadWhatsappConnections = async () => {
    if (!clientId) return;
    
    try {
      const response = await apiService.get(`/whatsapp/client/${clientId}`);
      const connections = Array.isArray(response) 
        ? response 
        : response?.connections || response?.data?.connections || [];
      
      // Filtrar apenas conexões oficiais e ativas
      const officialConnections = connections
        .filter((conn: any) => conn.connection_type === 'oficial' && conn.is_active)
        .map((conn: any) => ({ id: conn.id, name: conn.name }));
      
      setWhatsappConnections(officialConnections);
    } catch (error) {
      console.error('Erro ao carregar conexões WhatsApp:', error);
    }
  };

  const loadMenus = async () => {
    if (!clientId) return;
    
    setIsLoading(true);
    try {
      const data = await biApi.getInteractiveMenus({ clientId });
      setMenus(data);
    } catch (error) {
      console.error('Erro ao carregar menus:', error);
      toast({
        title: 'Erro',
        description: 'Não foi possível carregar os menus',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(event.active.id as string);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    
    if (!over || active.id === over.id) {
      setActiveId(null);
      return;
    }

    // Reordenar seções
    const activeIdStr = active.id as string;
    const overIdStr = over.id as string;
    
    if (activeIdStr.startsWith('section_') && overIdStr.startsWith('section_')) {
      const activeIndex = formData.sections.findIndex(s => s.id === activeIdStr);
      const overIndex = formData.sections.findIndex(s => s.id === overIdStr);
      
      if (activeIndex !== -1 && overIndex !== -1) {
        const newSections = [...formData.sections];
        const [removed] = newSections.splice(activeIndex, 1);
        newSections.splice(overIndex, 0, removed);
        setFormData({ ...formData, sections: newSections });
      }
    }
    
    // Reordenar rows dentro de uma seção
    if (activeIdStr.startsWith('row_') && overIdStr.startsWith('row_')) {
      // Formato: "row_{sectionId}_{rowIndex}"
      const activeMatch = activeIdStr.match(/^row_(.+)_(\d+)$/);
      const overMatch = overIdStr.match(/^row_(.+)_(\d+)$/);
      
      if (activeMatch && overMatch) {
        const [, activeSectionId, activeRowIndexStr] = activeMatch;
        const [, overSectionId, overRowIndexStr] = overMatch;
        
        if (activeSectionId === overSectionId) {
          const activeRowIndex = parseInt(activeRowIndexStr);
          const overRowIndex = parseInt(overRowIndexStr);
          
          const sectionIndex = formData.sections.findIndex(s => s.id === activeSectionId);
          if (sectionIndex !== -1 && !isNaN(activeRowIndex) && !isNaN(overRowIndex)) {
            const section = formData.sections[sectionIndex];
            const newRows = [...section.rows];
            
            if (activeRowIndex >= 0 && activeRowIndex < newRows.length &&
                overRowIndex >= 0 && overRowIndex < newRows.length) {
              const [removed] = newRows.splice(activeRowIndex, 1);
              newRows.splice(overRowIndex, 0, removed);
              
              const newSections = [...formData.sections];
              newSections[sectionIndex] = { ...section, rows: newRows };
              setFormData({ ...formData, sections: newSections });
            }
          }
        }
      }
    }
    
    setActiveId(null);
  };

  const handleAddSection = () => {
    if (formData.sections.length >= 10) {
      toast({
        title: 'Limite atingido',
        description: 'Você pode ter no máximo 10 seções',
        variant: 'destructive',
      });
      return;
    }

    const newSection: InteractiveMenuSection = {
      id: `section_${Date.now()}`,
      title: '',
      rows: []
    };

    setFormData({
      ...formData,
      sections: [...formData.sections, newSection]
    });
  };

  const handleAddRow = (sectionId: string) => {
    const totalRows = formData.sections.reduce((sum, s) => sum + (s.rows?.length || 0), 0);
    if (totalRows >= 10) {
      toast({
        title: 'Limite atingido',
        description: 'Você pode ter no máximo 10 rows no total',
        variant: 'destructive',
      });
      return;
    }

    const sectionIndex = formData.sections.findIndex(s => s.id === sectionId);
    if (sectionIndex !== -1) {
      const section = formData.sections[sectionIndex];
      if (section.rows.length >= 10) {
        toast({
          title: 'Limite atingido',
          description: 'Você pode ter no máximo 10 rows por seção',
          variant: 'destructive',
        });
        return;
      }

      const newRow: InteractiveMenuRow = {
        id: `row_${section.rows.length + 1}`,
        title: '',
        description: ''
      };

      const newSections = [...formData.sections];
      newSections[sectionIndex] = {
        ...section,
        rows: [...section.rows, newRow]
      };

      setFormData({ ...formData, sections: newSections });
    }
  };

  const handleUpdateSection = (sectionId: string, updatedSection: InteractiveMenuSection) => {
    const sectionIndex = formData.sections.findIndex(s => s.id === sectionId);
    if (sectionIndex !== -1) {
      const newSections = [...formData.sections];
      newSections[sectionIndex] = updatedSection;
      setFormData({ ...formData, sections: newSections });
    }
  };

  const handleDeleteSection = (sectionId: string) => {
    setFormData({
      ...formData,
      sections: formData.sections.filter(s => s.id !== sectionId)
    });
  };

  const handleUpdateRow = (sectionId: string, rowIndex: number, updatedRow: InteractiveMenuRow) => {
    const sectionIndex = formData.sections.findIndex(s => s.id === sectionId);
    if (sectionIndex !== -1) {
      const section = formData.sections[sectionIndex];
      const newRows = [...section.rows];
      newRows[rowIndex] = updatedRow;
      
      const newSections = [...formData.sections];
      newSections[sectionIndex] = { ...section, rows: newRows };
      setFormData({ ...formData, sections: newSections });
    }
  };

  const handleDeleteRow = (sectionId: string, rowIndex: number) => {
    const sectionIndex = formData.sections.findIndex(s => s.id === sectionId);
    if (sectionIndex !== -1) {
      const section = formData.sections[sectionIndex];
      const newRows = section.rows.filter((_, idx) => idx !== rowIndex);
      
      const newSections = [...formData.sections];
      newSections[sectionIndex] = { ...section, rows: newRows };
      setFormData({ ...formData, sections: newSections });
    }
  };

  const handleCreate = () => {
    setEditingMenu(null);
    setFormData({
      name: '',
      description: '',
      button_text: '',
      header_text: '',
      has_header: false,
      body_text: '',
      footer_text: '',
      has_footer: false,
      sections: []
    });
    setIsModalOpen(true);
  };

  const handleEdit = (menu: InteractiveMenu) => {
    setEditingMenu(menu);
    setFormData({
      name: menu.name,
      description: menu.description || '',
      button_text: menu.button_text,
      header_text: menu.header_text || '',
      has_header: !!menu.header_text,
      body_text: menu.body_text,
      footer_text: menu.footer_text || '',
      has_footer: !!menu.footer_text,
      sections: menu.sections || []
    });
    setIsModalOpen(true);
  };

  const handleDuplicate = async (menu: InteractiveMenu) => {
    if (!clientId) return;

    try {
      const duplicatedMenu = await biApi.createInteractiveMenu({
        client_id: clientId,
        name: `${menu.name} (Cópia)`,
        description: menu.description || undefined,
        button_text: menu.button_text,
        header_text: menu.header_text || undefined,
        body_text: menu.body_text,
        footer_text: menu.footer_text || undefined,
        sections: menu.sections || [],
        is_active: false, // Desativar por padrão ao duplicar
      });

      toast({
        title: 'Sucesso',
        description: 'Menu duplicado com sucesso',
      });
      
      loadMenus();
    } catch (error: any) {
      console.error('Erro ao duplicar menu:', error);
      toast({
        title: 'Erro',
        description: error?.response?.data?.error || 'Não foi possível duplicar o menu',
        variant: 'destructive',
      });
    }
  };

  const handleDelete = async (menu: InteractiveMenu) => {
    if (!confirm(`Tem certeza que deseja excluir o menu "${menu.name}"?`)) {
      return;
    }

    try {
      await biApi.deleteInteractiveMenu(menu.id);
      toast({
        title: 'Sucesso',
        description: 'Menu excluído com sucesso',
      });
      loadMenus();
    } catch (error: any) {
      console.error('Erro ao excluir menu:', error);
      toast({
        title: 'Erro',
        description: error?.response?.data?.error || 'Não foi possível excluir o menu',
        variant: 'destructive',
      });
    }
  };

  const handleToggleActive = async (menu: InteractiveMenu) => {
    try {
      await biApi.updateInteractiveMenu(menu.id, {
        is_active: !menu.is_active,
      });
      toast({
        title: 'Sucesso',
        description: `Menu ${!menu.is_active ? 'ativado' : 'desativado'} com sucesso`,
      });
      loadMenus();
    } catch (error: any) {
      console.error('Erro ao alterar status do menu:', error);
      toast({
        title: 'Erro',
        description: 'Não foi possível alterar o status do menu',
        variant: 'destructive',
      });
    }
  };

  const validateForm = (): string | null => {
    if (!formData.name.trim()) {
      return 'Nome do menu é obrigatório';
    }
    if (!formData.button_text.trim()) {
      return 'Texto do botão é obrigatório';
    }
    if (formData.button_text.length > 20) {
      return 'Texto do botão deve ter no máximo 20 caracteres';
    }
    if (!formData.body_text.trim()) {
      return 'Corpo da mensagem é obrigatório';
    }
    if (formData.body_text.length > 4096) {
      return 'Corpo da mensagem deve ter no máximo 4096 caracteres';
    }
    if (formData.has_header && formData.header_text.length > 60) {
      return 'Header deve ter no máximo 60 caracteres';
    }
    if (formData.has_footer && formData.footer_text.length > 60) {
      return 'Footer deve ter no máximo 60 caracteres';
    }
    if (formData.sections.length === 0) {
      return 'Adicione pelo menos uma seção';
    }
    
    const totalRows = formData.sections.reduce((sum, s) => sum + (s.rows?.length || 0), 0);
    if (totalRows === 0) {
      return 'Adicione pelo menos uma row';
    }
    if (totalRows > 10) {
      return 'Você pode ter no máximo 10 rows no total';
    }

    // Validar cada seção
    for (const section of formData.sections) {
      if (!section.title.trim()) {
        return `Seção ${formData.sections.indexOf(section) + 1}: título é obrigatório`;
      }
      if (section.title.length > 24) {
        return `Seção ${formData.sections.indexOf(section) + 1}: título deve ter no máximo 24 caracteres`;
      }
      
      // Validar cada row
      for (const row of section.rows) {
        if (!row.id.trim()) {
          return `Row sem ID na seção "${section.title}"`;
        }
        if (row.id.length > 200) {
          return `Row ID muito longo na seção "${section.title}"`;
        }
        if (!row.title.trim()) {
          return `Row sem título na seção "${section.title}"`;
        }
        if (row.title.length > 24) {
          return `Row título muito longo na seção "${section.title}"`;
        }
        if (row.description && row.description.length > 72) {
          return `Row descrição muito longa na seção "${section.title}"`;
        }
      }
    }

    return null;
  };

  const handleSave = async () => {
    const error = validateForm();
    if (error) {
      toast({
        title: 'Erro de validação',
        description: error,
        variant: 'destructive',
      });
      return;
    }

    if (!clientId) return;

    try {
      if (editingMenu) {
        await biApi.updateInteractiveMenu(editingMenu.id, {
          name: formData.name,
          description: formData.description || undefined,
          button_text: formData.button_text,
          header_text: formData.has_header ? formData.header_text : undefined,
          body_text: formData.body_text,
          footer_text: formData.has_footer ? formData.footer_text : undefined,
          sections: formData.sections,
        });
        toast({
          title: 'Sucesso',
          description: 'Menu atualizado com sucesso',
        });
      } else {
        await biApi.createInteractiveMenu({
          client_id: clientId,
          name: formData.name,
          description: formData.description || undefined,
          button_text: formData.button_text,
          header_text: formData.has_header ? formData.header_text : undefined,
          body_text: formData.body_text,
          footer_text: formData.has_footer ? formData.footer_text : undefined,
          sections: formData.sections,
          is_active: true,
        });
        toast({
          title: 'Sucesso',
          description: 'Menu criado com sucesso',
        });
      }
      
      setIsModalOpen(false);
      loadMenus();
    } catch (error: any) {
      console.error('Erro ao salvar menu:', error);
      toast({
        title: 'Erro',
        description: error?.response?.data?.error || 'Não foi possível salvar o menu',
        variant: 'destructive',
      });
    }
  };

  const totalRows = formData.sections.reduce((sum, s) => sum + (s.rows?.length || 0), 0);

  if (!clientId) {
    return (
      <Card>
        <CardContent className="p-12 text-center">
          <p className="text-gray-500">Selecione um cliente para gerenciar menus interativos</p>
        </CardContent>
      </Card>
    );
  }

  if (isLoading) {
    return <AtomicLoadingScreen />;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold">Menus Interativos WhatsApp</h3>
          <p className="text-sm text-gray-500">Crie e gerencie menus de lista interativa para primeiro contato</p>
        </div>
        <Button onClick={handleCreate}>
          <Plus className="h-4 w-4 mr-2" />
          Novo Menu
        </Button>
      </div>

      {/* Lista de Menus */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {menus.map((menu) => (
          <Card key={menu.id}>
            <CardHeader>
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <CardTitle className="text-base">{menu.name}</CardTitle>
                  {menu.description && (
                    <CardDescription className="mt-1">{menu.description}</CardDescription>
                  )}
                </div>
                <Badge variant={menu.is_active ? 'default' : 'secondary'}>
                  {menu.is_active ? 'Ativo' : 'Inativo'}
                </Badge>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-2 text-sm text-gray-600">
                <div>{menu.sections.length} seção{menu.sections.length !== 1 ? 'ões' : ''}</div>
                <div>{menu.sections.reduce((sum, s) => sum + (s.rows?.length || 0), 0)} row{menu.sections.reduce((sum, s) => sum + (s.rows?.length || 0), 0) !== 1 ? 's' : ''}</div>
                <div className="text-xs text-gray-500">
                  Atualizado: {new Date(menu.updated_at).toLocaleDateString('pt-BR')}
                </div>
              </div>
              <div className="flex gap-2 mt-4">
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => handleEdit(menu)}
                  title="Editar menu"
                >
                  <Edit className="h-4 w-4" />
                </Button>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => handleDuplicate(menu)}
                  title="Duplicar menu"
                >
                  <Copy className="h-4 w-4" />
                </Button>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => {
                    setSendingMenu(menu);
                    setIsSendModalOpen(true);
                  }}
                  title="Enviar teste"
                  disabled={!menu.is_active}
                >
                  <Send className="h-4 w-4" />
                </Button>
                <Button 
                  variant={menu.is_active ? "default" : "outline"}
                  size="sm"
                  onClick={() => handleToggleActive(menu)}
                  title={menu.is_active ? "Desativar menu" : "Ativar menu"}
                >
                  {menu.is_active ? 'Ativo' : 'Inativo'}
                </Button>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => handleDelete(menu)}
                  title="Excluir menu"
                  className="text-destructive hover:text-destructive"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
        
        {menus.length === 0 && (
          <div className="col-span-full text-center py-12 text-gray-500">
            <p className="text-lg font-medium mb-2">Nenhum menu criado</p>
            <p className="text-sm mb-4">Comece criando seu primeiro menu interativo</p>
            <Button onClick={handleCreate} variant="outline">
              <Plus className="h-4 w-4 mr-2" />
              Criar Primeiro Menu
            </Button>
          </div>
        )}
      </div>

      {/* Modal de Criação/Edição */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingMenu ? 'Editar Menu' : 'Novo Menu Interativo'}</DialogTitle>
            <DialogDescription>
              Configure o menu de lista interativa que será enviado no primeiro contato
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 mt-4">
            {/* Nome e Descrição */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Nome do Menu *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Ex: Menu Principal"
                />
              </div>
              <div>
                <Label>Descrição</Label>
                <Input
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Descrição opcional"
                />
              </div>
            </div>

            {/* Bloco Button Text */}
            <Card className="border-l-4 border-l-purple-500">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm flex items-center gap-2">
                  📝 Texto do Botão *
                </CardTitle>
              </CardHeader>
              <CardContent>
                <Input
                  value={formData.button_text}
                  onChange={(e) => {
                    const value = e.target.value.slice(0, 20);
                    setFormData({ ...formData, button_text: value });
                  }}
                  placeholder="Ex: Opções"
                  maxLength={20}
                />
                <div className="flex items-center justify-between mt-1">
                  <span className={`text-xs ${formData.button_text.length > 20 ? 'text-red-500' : 'text-gray-500'}`}>
                    {formData.button_text.length}/20
                  </span>
                  {formData.button_text.length === 20 && (
                    <span className="text-xs text-red-500 flex items-center gap-1">
                      <AlertCircle className="h-3 w-3" />
                      Limite atingido
                    </span>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Bloco Header */}
            <Card className="border-l-4 border-l-blue-500">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm flex items-center gap-2">
                    📄 Header
                  </CardTitle>
                  <div className="flex items-center gap-2">
                    <Label className="text-sm">Incluir Header</Label>
                    <Switch
                      checked={formData.has_header}
                      onCheckedChange={(checked) => {
                        setFormData({ 
                          ...formData, 
                          has_header: checked,
                          header_text: checked ? formData.header_text : ''
                        });
                      }}
                    />
                  </div>
                </div>
              </CardHeader>
              {formData.has_header && (
                <CardContent>
                  <Input
                    value={formData.header_text}
                    onChange={(e) => {
                      const value = e.target.value.slice(0, 60);
                      setFormData({ ...formData, header_text: value });
                    }}
                    placeholder="Ex: Bem-vindo!"
                    maxLength={60}
                  />
                  <span className={`text-xs mt-1 block ${formData.header_text.length > 60 ? 'text-red-500' : 'text-gray-500'}`}>
                    {formData.header_text.length}/60
                  </span>
                </CardContent>
              )}
            </Card>

            {/* Bloco Body */}
            <Card className="border-l-4 border-l-green-500">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm flex items-center gap-2">
                  💬 Corpo da Mensagem *
                </CardTitle>
              </CardHeader>
              <CardContent>
                <Textarea
                  value={formData.body_text}
                  onChange={(e) => {
                    const value = e.target.value.slice(0, 4096);
                    setFormData({ ...formData, body_text: value });
                  }}
                  placeholder="Digite a mensagem principal..."
                  rows={6}
                  maxLength={4096}
                />
                <span className={`text-xs mt-1 block ${formData.body_text.length > 4096 ? 'text-red-500' : 'text-gray-500'}`}>
                  {formData.body_text.length}/4096
                </span>
              </CardContent>
            </Card>

            {/* Bloco Sections */}
            <Card className="border-l-4 border-l-orange-500">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm flex items-center gap-2">
                    📋 Seções do Menu
                  </CardTitle>
                  <Badge variant="secondary">
                    {formData.sections.length}/10 seções • {totalRows}/10 rows
                  </Badge>
                </div>
              </CardHeader>
              <CardContent>
                <DndContext
                  sensors={[sensors]}
                  collisionDetection={closestCenter}
                  onDragStart={handleDragStart}
                  onDragEnd={handleDragEnd}
                >
                  <SortableContext items={formData.sections.map(s => s.id)} strategy={verticalListSortingStrategy}>
                    <div className="space-y-4">
                      {formData.sections.map((section, index) => (
                        <SortableSection
                          key={section.id}
                          section={section}
                          index={index}
                          totalRows={totalRows}
                          onUpdate={(updated) => handleUpdateSection(section.id, updated)}
                          onDelete={() => handleDeleteSection(section.id)}
                          onAddRow={() => handleAddRow(section.id)}
                          onUpdateRow={(rowIndex, row) => handleUpdateRow(section.id, rowIndex, row)}
                          onDeleteRow={(rowIndex) => handleDeleteRow(section.id, rowIndex)}
                        />
                      ))}
                    </div>
                  </SortableContext>
                  <DragOverlay>
                    {activeId ? (
                      <div className="opacity-50">
                        {/* Preview do item sendo arrastado */}
                      </div>
                    ) : null}
                  </DragOverlay>
                </DndContext>

                <Button
                  variant="outline"
                  onClick={handleAddSection}
                  disabled={formData.sections.length >= 10}
                  className="w-full mt-4"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Adicionar Seção
                  {formData.sections.length >= 10 && ' (limite atingido)'}
                </Button>
              </CardContent>
            </Card>

            {/* Bloco Footer */}
            <Card className="border-l-4 border-l-pink-500">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm flex items-center gap-2">
                    📌 Footer
                  </CardTitle>
                  <div className="flex items-center gap-2">
                    <Label className="text-sm">Incluir Footer</Label>
                    <Switch
                      checked={formData.has_footer}
                      onCheckedChange={(checked) => {
                        setFormData({ 
                          ...formData, 
                          has_footer: checked,
                          footer_text: checked ? formData.footer_text : ''
                        });
                      }}
                    />
                  </div>
                </div>
              </CardHeader>
              {formData.has_footer && (
                <CardContent>
                  <Input
                    value={formData.footer_text}
                    onChange={(e) => {
                      const value = e.target.value.slice(0, 60);
                      setFormData({ ...formData, footer_text: value });
                    }}
                    placeholder="Ex: Atendimento 24h"
                    maxLength={60}
                  />
                  <span className={`text-xs mt-1 block ${formData.footer_text.length > 60 ? 'text-red-500' : 'text-gray-500'}`}>
                    {formData.footer_text.length}/60
                  </span>
                </CardContent>
              )}
            </Card>
          </div>

          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setIsPreviewOpen(true)}
            >
              <Eye className="h-4 w-4 mr-2" />
              Preview
            </Button>
            <Button variant="outline" onClick={() => setIsModalOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={handleSave}>
              <Save className="h-4 w-4 mr-2" />
              {editingMenu ? 'Atualizar' : 'Criar'} Menu
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal de Preview */}
      <Dialog open={isPreviewOpen} onOpenChange={setIsPreviewOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Preview do Menu</DialogTitle>
            <DialogDescription>
              Visualização de como o menu aparecerá no WhatsApp
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            {/* Simulação WhatsApp */}
            <div className="bg-[#e5ddd5] p-4 rounded-lg space-y-3">
              {/* Header */}
              {formData.has_header && formData.header_text && (
                <div className="bg-white p-3 rounded-lg shadow-sm">
                  <p className="text-sm font-semibold text-gray-800">{formData.header_text}</p>
                </div>
              )}
              
              {/* Body */}
              <div className="bg-white p-3 rounded-lg shadow-sm">
                <p className="text-sm text-gray-800 whitespace-pre-wrap">{formData.body_text}</p>
              </div>
              
              {/* Button */}
              <div className="bg-[#25d366] p-3 rounded-lg shadow-sm text-center">
                <button className="text-white font-medium text-sm w-full">
                  {formData.button_text || 'Opções'}
                </button>
              </div>
              
              {/* Sections Preview */}
              {formData.sections.length > 0 && (
                <div className="bg-white p-3 rounded-lg shadow-sm space-y-2">
                  <p className="text-xs text-gray-500 mb-2">Ao clicar no botão, aparecerá:</p>
                  {formData.sections.map((section, idx) => (
                    <div key={section.id} className="border-l-2 border-blue-500 pl-2">
                      <p className="text-xs font-semibold text-gray-700">{section.title}</p>
                      {section.rows.map((row, rowIdx) => (
                        <div key={rowIdx} className="ml-2 mt-1 text-xs text-gray-600">
                          • {row.title}
                          {row.description && (
                            <span className="text-gray-400"> - {row.description}</span>
                          )}
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              )}
              
              {/* Footer */}
              {formData.has_footer && formData.footer_text && (
                <div className="bg-white p-2 rounded-lg shadow-sm">
                  <p className="text-xs text-gray-500 text-center">{formData.footer_text}</p>
                </div>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal de Envio de Teste */}
      <Dialog open={isSendModalOpen} onOpenChange={setIsSendModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Enviar Menu de Teste</DialogTitle>
            <DialogDescription>
              Envie o menu "{sendingMenu?.name}" para um número de teste
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            <div>
              <Label>Número de Telefone *</Label>
              <Input
                value={sendPhoneNumber}
                onChange={(e) => setSendPhoneNumber(e.target.value)}
                placeholder="5511999999999"
              />
              <p className="text-xs text-gray-500 mt-1">
                Formato: 5511999999999 (código do país + DDD + número)
              </p>
            </div>
            
            <div>
              <Label>Conexão WhatsApp *</Label>
              <Select
                value={sendConnectionId?.toString() || ''}
                onValueChange={(value) => setSendConnectionId(parseInt(value))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Selecione uma conexão" />
                </SelectTrigger>
                <SelectContent>
                  {whatsappConnections.map((conn) => (
                    <SelectItem key={conn.id} value={conn.id.toString()}>
                      {conn.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {whatsappConnections.length === 0 && (
                <p className="text-xs text-red-500 mt-1">
                  Nenhuma conexão WhatsApp oficial encontrada
                </p>
              )}
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsSendModalOpen(false)}>
              Cancelar
            </Button>
            <Button
              onClick={async () => {
                if (!sendingMenu || !sendPhoneNumber || !sendConnectionId) {
                  toast({
                    title: 'Erro',
                    description: 'Preencha todos os campos',
                    variant: 'destructive',
                  });
                  return;
                }

                try {
                  await biApi.sendInteractiveMenu(
                    sendingMenu.id,
                    sendPhoneNumber,
                    sendConnectionId
                  );
                  toast({
                    title: 'Sucesso',
                    description: 'Menu enviado com sucesso!',
                  });
                  setIsSendModalOpen(false);
                  setSendPhoneNumber('');
                  setSendConnectionId(null);
                } catch (error: any) {
                  console.error('Erro ao enviar menu:', error);
                  toast({
                    title: 'Erro',
                    description: error?.response?.data?.error || 'Não foi possível enviar o menu',
                    variant: 'destructive',
                  });
                }
              }}
            >
              <Send className="h-4 w-4 mr-2" />
              Enviar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default InteractiveMenuBuilder;

